import EventEmitter from 'eventemitter3';
import { z } from 'zod';
import {
    WebsocketRequestDto,
    WebsocketResponseDto,
    WebsocketResponseKnownTypes,
    WebsocketResponseSchema,
    WebsocketSubscribeRequestSchema,
    WebsocketUnknownResponse,
    WebsocketUnsubscribeRequestSchema,
    WebsocketUpdateSessionSchema,
} from '@/lib/api/schemas/notification.schema';
import { v4 as uuid } from 'uuid';
import { isString } from 'underscore';

export type WSEvent = [id: string | null, topic: string | null, payload: any];

interface WSEvents {
    connected: [];
    disconnected: [clean: boolean];
    event: WSEvent;
    // "error": [error: any];
}

export abstract class WebsocketClient<
    E extends EventEmitter.ValidEventTypes = WSEvents,
> extends EventEmitter<E | WSEvents> {
    protected subscriptions: Record<string, string[]> = {};
    protected _accessToken: string = '';
    abstract connect(accessToken: string): Promise<void>;
    abstract disconnect(): Promise<void>;
    abstract send(request: WebsocketRequestDto): Promise<void>;
    async updateSession(accessToken: string): Promise<void> {
        this._accessToken = accessToken;
        const req: z.infer<typeof WebsocketUpdateSessionSchema> = {
            type: 'update-session',
            accessToken,
        };
        await this.send(req);
    }
    protected async subscribeReq(
        topic: string | [string, ...string[]],
    ): Promise<void> {
        const req: z.infer<typeof WebsocketSubscribeRequestSchema> = {
            type: 'subscribe',
            topic,
        };
        await this.send(req);
    }
    protected async unsubscribeReq(
        topic: string | [string, ...string[]],
    ): Promise<void> {
        const req: z.infer<typeof WebsocketUnsubscribeRequestSchema> = {
            type: 'unsubscribe',
            topic,
        };
        await this.send(req);
    }

    protected async unsubscribe(
        sid: string,
        topics: [string, ...string[]],
    ): Promise<void> {
        const freedTopics: string[] = [];
        for (const topic of topics) {
            if (!this.subscriptions[topic]) {
                continue;
            }
            if (this.subscriptions[topic].includes(sid)) {
                this.subscriptions[topic] = this.subscriptions[topic].filter(
                    (s) => s !== sid,
                );
            }
            if (this.subscriptions[topic].length < 1) {
                delete this.subscriptions[topic];
                freedTopics.push(topic);
            }
        }
        if (freedTopics.length > 0) {
            await this.unsubscribeReq(freedTopics as [string, ...string[]]);
        }
    }

    async subscribe(
        topics: string | [string, ...string[]],
    ): Promise<() => Promise<void>> {
        if (isString(topics)) topics = [topics];
        const newTopics: string[] = [];
        const sid = uuid();
        for (const topic of topics) {
            if (!this.subscriptions[topic]) {
                this.subscriptions[topic] = [];
            }
            if (this.subscriptions[topic].length < 1) {
                newTopics.push(topic);
            }
            this.subscriptions[topic].push(sid);
        }
        if (newTopics.length > 0) {
            // TODO handle error and return failed topics if partial failure on server side.
            await this.subscribeReq(newTopics as [string, ...string[]]);
        }
        return () => {
            return this.unsubscribe(sid, topics);
        };
    }
}

const DEFAULT_TIMEOUT = 60 * 1000;
const PING_INTERVAL = 30 * 1000;
const PING_JITTER = 6 * 1000;
type RequestResponseCallback = (error: Error | null, response: any) => void;

function wrapCatchWarning(run: () => void) {
    try {
        run();
    } catch (e: any) {
        console.error('!!!!!!!!!');
        console.error(
            '!!!!!!!!! Please do not throw errors in websocket client event listeners,',
        );
        console.error('!!!!!!!!! it prevents other listeners from working');
        console.error(e);
        console.error('!!!!!!!!!');
    }
}

// TODO detect disconnected socket if pong or event not received for X time?
// TODO send with retry? push everything to pending and drain? hmm
export class WebsocketClientSimple<
    E extends EventEmitter.ValidEventTypes = WSEvents,
> extends WebsocketClient<E> {
    private socket: WebSocket | null = null;
    private connected: boolean = false; // TODO status enum?
    private shouldConnect: boolean = false;
    constructor(private readonly remote: string) {
        super();
    }
    private inflight: Record<string, RequestResponseCallback> = {};
    private pending: WebsocketRequestDto[] = [];

    private schedulePing() {
        if (!this.socket) return;
        setTimeout(
            () => {
                if (!this.socket) return;
                this.socket.send('ping');
                this.schedulePing();
            },
            PING_INTERVAL + (Math.random() - 0.5) * PING_JITTER,
        );
    }

    private _connected() {
        this.connected = true;
        // TODO if there are many pending requests maybe add some small sleep?
        while (this.pending.length > 0) {
            const req = this.pending[0];
            try {
                this.socket!.send(JSON.stringify(req));
            } catch (e: any) {
                this._inflightCb(
                    req.rid,
                    new Error('failed sending request', e),
                    null,
                );
                // TODO ?
            }
        }
        this.pending.length = 0;

        const resub = Object.keys(this.subscriptions);
        if (resub.length > 0) {
            this.subscribeReq(resub as [string, ...string[]]).then(() =>
                wrapCatchWarning(() => {
                    this.emit('connected');
                }),
            );
        } else {
            wrapCatchWarning(() => {
                this.emit('connected');
            });
        }
    }
    private _inflightCb(
        rid: string | undefined,
        error: Error | null,
        response: any,
    ) {
        if (!rid || !(rid in this.inflight)) return false;
        try {
            this.inflight[rid](error, response);
        } catch (e) {
            console.error('Error calling inflight callback', e);
        } finally {
            // TODO maybe allow multi-response for rid at some point? silly.
            delete this.inflight[rid];
        }
        return true;
    }
    private _message(json: any) {
        if (json?.type === 'hello') {
            this._connected();
            return;
        }
        // typescript does not allow restricted strings - meh
        const known = WebsocketResponseKnownTypes.includes(json?.type);
        const parsedRaw = known
            ? WebsocketResponseSchema.parse(json)
            : WebsocketUnknownResponse.parse(json);
        if (json?.rid && json.rid in this.inflight) {
            // TODO better error
            const error =
                json.type === 'error'
                    ? new Error(json.error ?? 'Unknown error occurred.')
                    : null;
            this._inflightCb(json.rid, error, parsedRaw);
        }
        if (known) {
            const parsed = parsedRaw as WebsocketResponseDto;
            if (parsed.type === 'event') {
                wrapCatchWarning(() => {
                    this.emit(
                        'event',
                        parsed.id ?? null,
                        parsed.topic ?? null,
                        parsed.payload,
                    );
                });
                return;
            }
        }
        // TODO emit unknown message event?
        // console.log(json);
    }

    async connect(accessToken: string): Promise<void> {
        this.shouldConnect = true;
        if (this.connected || this.socket) {
            if (this._accessToken !== accessToken) {
                return await this.updateSession(accessToken);
            }
            return;
        }
        this._accessToken = accessToken;
        // TODO I hate passing accessToken like this, maybe should encrypt or obfuscate it :/
        const socket = new WebSocket(
            `${this.remote}?accessToken=${this._accessToken}`,
        );
        this.socket = socket;
        socket.onopen = () => {
            if (!this.shouldConnect) {
                socket?.close();
            }
        };
        socket.onmessage = ({ data }: MessageEvent) => {
            if (!isString(data)) data = new TextDecoder().decode(data);
            if (data === 'ping') {
                socket.send('pong');
            } else if (data === 'pong') {
                return;
            }
            const parsed = JSON.parse(data);
            this._message(parsed);
        };
        socket.onclose = ({ code, reason }: CloseEvent) => {
            this.connected = false;
            if (this.socket === socket) this.socket = null;
            if (!this.shouldConnect) {
                return;
            }
            // TODO reconnect
        };
        this.schedulePing();
    }
    disconnect(): Promise<void> {
        this.shouldConnect = false;
        if (this.socket) {
            this.socket.close();
        }
        return Promise.resolve(undefined);
    }
    send(
        request: WebsocketRequestDto,
        timeout: number = DEFAULT_TIMEOUT,
    ): Promise<void> {
        const rid = request.rid ?? uuid();
        const copied: WebsocketRequestDto = { ...request, rid };
        let pending = false;
        let callback: RequestResponseCallback;
        const promise = new Promise<void>((resolve, reject) => {
            callback = (error, response) => {
                delete this.inflight[rid];
                if (error) return reject(error);
                resolve(response);
            };
            setTimeout(
                () => {
                    delete this.inflight[rid];
                    if (pending) {
                        const idx = this.pending
                            .map((e) => {
                                return e.rid;
                            })
                            .indexOf(rid);
                        if (idx !== -1) {
                            this.pending.splice(idx, 1);
                        }
                    }
                    reject(new Error(`Request ${rid} timed out.`));
                },
                this.connected ? timeout : timeout * 2,
            );
        });
        this.inflight[rid] = callback!;
        if (this.connected) {
            try {
                this.socket!.send(JSON.stringify(copied));
            } catch (e: any) {
                callback!(new Error('failed sending request', e), null);
            }
        } else {
            pending = true;
            this.pending.push(copied);
        }
        return promise;
    }
}
